import { useCountryContext } from '@archipro-website/localisation';
import {
    REGION_OPTIONS_BY_COUNTRY,
    type RegionOption,
} from '~/modules/get-matched/constants/regions';
import { SelectableCard } from './SelectableCard';
import { StepHeading, StepHelperText } from './StepText';

/**
 * Worst-case 3 longest NZ/AU labels join to ~67 chars, comfortably under the
 * Region column's Varchar(128) cap. Bumping this requires widening the DB.
 */
const MAX_MULTI_SELECTIONS = 3;

interface Step2LocationProps {
    title: string;
    helperText?: string;
    /** When true the cards toggle and emit a comma-separated list of labels. */
    multiSelect: boolean;
    /**
     * For single-select this is the chosen label (or null).
     * For multi-select this is the joined `"Auckland, Wellington"` string.
     */
    value: string | null;
    onSelect: (label: string) => void;
}

const splitJoined = (raw: string | null): string[] =>
    raw
        ? raw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
        : [];

export const Step2Location = ({
    title,
    helperText,
    multiSelect,
    value,
    onSelect,
}: Step2LocationProps) => {
    const { code: countryCode } = useCountryContext();
    const options: RegionOption[] =
        REGION_OPTIONS_BY_COUNTRY[countryCode] ?? REGION_OPTIONS_BY_COUNTRY.NZ!;

    const selected = multiSelect ? splitJoined(value) : value ? [value] : [];

    const handleClick = (label: string) => {
        if (!multiSelect) {
            onSelect(label);
            return;
        }
        if (selected.includes(label)) {
            // De-selecting is always allowed.
            onSelect(selected.filter((s) => s !== label).join(', '));
            return;
        }
        if (selected.length >= MAX_MULTI_SELECTIONS) {
            // At cap — refuse silently. Helper text already tells the user about the limit.
            return;
        }
        onSelect([...selected, label].join(', '));
    };

    return (
        <div className="flex flex-col gap-[1.375rem]">
            <StepHeading>{title}</StepHeading>
            {helperText && <StepHelperText>{helperText}</StepHelperText>}
            <ul className="flex flex-col gap-2">
                {options.map((option) => (
                    <li key={option.label}>
                        <SelectableCard
                            title={option.label}
                            selected={selected.includes(option.label)}
                            onClick={() => handleClick(option.label)}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
};
